import { action, makeObservable, observable } from "mobx";
import apiService from "./apiService";
import type { PromiseCompletion } from "../classes/PromiseCompletion";

/** LLM tier rates — USD per 1M tokens. */
export type LlmTierRate = { input: number; output: number; cacheRead: number };
export type ImageTierRate = { perMegapixel: number };
export type VideoTierRate = { perSecond: number };

/**
 * Client mirror of the server's pricing config (fetched from GET /api/pricing).
 * Used to show per-token rates (e.g. "$2.00/1M input") inside cost breakdown popups.
 */
export type PricingRates = {
  enabled: boolean;
  currency: string;
  llm: { tiers: Record<string, LlmTierRate>; modelTiers: Record<string, string>; defaultTier: string };
  image: { tiers: Record<string, ImageTierRate>; modelTiers: Record<string, string>; defaultTier: string };
  video: { tiers: Record<string, VideoTierRate>; resolutionMultipliers: Record<string, number>; modelTiers: Record<string, string>; defaultTier: string };
};

function patternMatches(pattern: string, key: string): boolean {
  if (pattern === '*') return true;
  const normalized = key.toLowerCase();
  if (pattern.endsWith('*') && !pattern.includes('*', pattern.length - 1)) {
    return normalized.startsWith(pattern.slice(0, -1).toLowerCase());
  }
  const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, '\\$&').replace(/\*/g, '.*');
  return new RegExp(`^${escaped}$`, 'i').test(normalized);
}

class PricingService {
  @observable public loaded: boolean = false;
  @observable public rates: PricingRates | null = null;
  private _loading: Promise<void> | null = null;

  constructor() {
    makeObservable(this);
  }

  /** Fetch pricing rates once (no-op if already loaded / loading). Safe to call from multiple components. */
  public load = (loader?: PromiseCompletion): void => {
    if (this.loaded || this._loading) return;
    this._loading = (async () => {
      const data = await apiService.get<PricingRates>('/api/pricing', { loader, suppress404: true });
      action(() => {
        this.rates = data;
        this.loaded = true;
      })();
    })().finally(() => { this._loading = null; });
  };

  /** Resolve the LLM tier name for a model via the wildcard modelTiers map (falls back to defaultTier). */
  public resolveLlmTier(modelName: string): string | null {
    const llm = this.rates?.llm;
    if (!llm) return null;
    for (const [pattern, tier] of Object.entries(llm.modelTiers)) {
      if (patternMatches(pattern, modelName || '')) return tier;
    }
    return llm.defaultTier ?? null;
  }

  /** Rates for a model's LLM tier, or null if unknown / pricing not loaded. */
  public getLlmRates(modelName: string): LlmTierRate | null {
    const llm = this.rates?.llm;
    if (!llm) return null;
    const tier = this.resolveLlmTier(modelName);
    return tier ? (llm.tiers[tier] ?? null) : null;
  }
}

export const pricingService = new PricingService();
